import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PlusIcon, ArrowLeftIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useSubcollection } from '../hooks/useFirestore';
import DocumentTable from '../components/DocumentTable';
import DocumentModal from '../components/DocumentModal';
import BookModal from '../components/BookModal';
import ConfirmDialog from '../components/ConfirmDialog';

const SubcollectionPage = () => {
  const { collectionName, documentId, subcollectionName } = useParams();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [modalMode, setModalMode] = useState('add');
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const {
    documents,
    loading,
    addDocument,
    updateDocument,
    deleteDocument
  } = useSubcollection(collectionName, documentId, subcollectionName);

  const handleAdd = () => {
    setSelectedDocument(null);
    setModalMode('add');
    setModalOpen(true);
  };

  const handleEdit = (doc) => {
    setSelectedDocument(doc);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleView = (doc) => {
    setSelectedDocument(doc);
    setModalMode('view');
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteDocument(deleteId);
      setConfirmDialogOpen(false);
      setDeleteId(null);
    } catch (error) {
      console.error('Error deleting document:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSave = async (data) => {
    if (modalMode === 'add') {
      await addDocument(data);
    } else if (modalMode === 'edit') {
      await updateDocument(selectedDocument.id, data);
    }
  };



  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate(`/collection/${collectionName}`)}
              className="mr-4 p-2 rounded-md text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 capitalize">
                {subcollectionName}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Subcollection of {collectionName} → {documentId}
              </p>
              <nav className="flex" aria-label="Breadcrumb">
                <ol className="flex items-center space-x-2">
                  <li>
                    <div className="flex items-center">
                      <button
                        onClick={() => navigate('/')}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        Dashboard
                      </button>
                    </div>
                  </li>
                  <li>
                    <div className="flex items-center">
                      <svg
                        className="flex-shrink-0 h-5 w-5 text-gray-300"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <button
                        onClick={() => navigate(`/collection/${collectionName}`)}
                        className="ml-2 text-gray-400 hover:text-gray-500"
                      >
                        {collectionName}
                      </button>
                    </div>
                  </li>
                  <li>
                    <div className="flex items-center">
                      <svg
                        className="flex-shrink-0 h-5 w-5 text-gray-300"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="ml-2 text-gray-500">{subcollectionName}</span>
                    </div>
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          <button
            onClick={handleAdd}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            {subcollectionName === 'books' ? 'Add New Book' : 'Add Document'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-medium text-sm">
                    {documents.length}
                  </span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Documents
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {documents.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-medium text-sm">
                    SC
                  </span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Parent Document
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {documentId.substring(0, 8)}...
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  {loading ? (
                    <ExclamationTriangleIcon className="h-4 w-4 text-purple-600" />
                  ) : (
                    <CheckCircleIcon className="h-4 w-4 text-purple-600" />
                  )}
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Status
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {loading ? 'Loading' : 'Ready'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Documents Table */}
      <DocumentTable
        documents={documents}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onView={handleView}
        collectionName={`${collectionName}/${subcollectionName}`}
        hasSubcollections={false}
      />

      {/* Document Modal */}
      {subcollectionName === 'books' ? (
        <BookModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
          document={selectedDocument}
          mode={modalMode}
        />
      ) : (
        <DocumentModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
          document={selectedDocument}
          collectionName={subcollectionName}
          mode={modalMode}
        />
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Document"
        message="Are you sure you want to delete this document? This action cannot be undone."
        loading={deleteLoading}
      />
    </div>
  );
};

export default SubcollectionPage;
